/**
 * Tenant → Priority Support (Enterprise · `priority_support` flag)
 *
 * Shows dedicated SLA contact card to tenants on Enterprise plan; upgrade
 * CTA for everyone else. Config is platform-wide, set by super admin.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, LifeBuoy, Mail, Phone, MessageCircle, ExternalLink, Clock } from "lucide-react";
import { PageHeader } from "@/components/Ui";

export default function PrioritySupportPage() {
    const [state, setState] = useState(null);
    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/tenant/priority-support"); setState(data); }
            catch (e) { toast.error(apiError(e)); }
        })();
    }, []);

    if (!state) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
    if (!state.enabled) return <FlagDisabled/>;

    const cfg = state.config || {};
    const body = (cfg.body_md || "").replace("{sla_hours}", cfg.sla_hours);

    return (
        <>
            <PageHeader title="Priority Support" subtitle="Direct line to our senior engineers — guaranteed SLA, dedicated channels."/>

            <div className="border border-primary/40 bg-primary/5 p-6 mb-6" data-testid="priority-banner">
                <div className="flex items-start gap-3">
                    <Clock className="w-6 h-6 text-primary mt-1 shrink-0"/>
                    <div>
                        <div className="text-lg font-serif text-primary">{cfg.sla_hours}-hour SLA</div>
                        <div className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{body}</div>
                    </div>
                </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
                {cfg.email && <ContactCard icon={Mail} label="Priority email" value={cfg.email} href={`mailto:${cfg.email}`} testId="contact-email"/>}
                {cfg.phone && <ContactCard icon={Phone} label="Direct phone" value={cfg.phone} href={`tel:${cfg.phone}`} testId="contact-phone"/>}
                {cfg.whatsapp && <ContactCard icon={MessageCircle} label="WhatsApp" value={cfg.whatsapp}
                    href={`https://wa.me/${cfg.whatsapp.replace(/\D/g, "")}`} testId="contact-whatsapp"/>}
            </div>

            {cfg.kb_url && (
                <a href={cfg.kb_url} target="_blank" rel="noopener noreferrer" data-testid="kb-link"
                    className="inline-flex items-center gap-2 mt-6 text-sm text-primary hover:text-amber-300">
                    Knowledge base & runbooks <ExternalLink className="w-3.5 h-3.5"/>
                </a>
            )}
        </>
    );
}

function ContactCard({ icon: Icon, label, value, href, testId }) {
    return (
        <a href={href} className="border border-border bg-card p-4 hover:border-primary transition-colors block"
            data-testid={testId}>
            <Icon className="w-5 h-5 text-primary mb-2"/>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
            <div className="text-sm text-foreground mt-1 break-all">{value}</div>
        </a>
    );
}

function FlagDisabled() {
    return (
        <>
            <PageHeader title="Priority Support"/>
            <div className="border border-border p-8 text-center" data-testid="flag-disabled">
                <Lock className="w-8 h-8 mx-auto text-border mb-3"/>
                <div className="font-serif text-xl mb-2">Priority Support — Enterprise plan</div>
                <div className="text-sm text-muted-foreground mb-2 max-w-md mx-auto">
                    Direct line to senior engineers, dedicated email/WhatsApp, and a guaranteed business-hour SLA.
                </div>
                <div className="text-xs text-muted-foreground mb-5">
                    <LifeBuoy className="w-3 h-3 inline mr-1"/>Standard support continues via the in-app chatbot.
                </div>
                <a href="/app/billing/plans" data-testid="upgrade-link"
                    className="inline-block px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.15em]">
                    Compare plans · Upgrade →
                </a>
            </div>
        </>
    );
}
